import {
  useDeletePartnerPaymentMethodMutation,
  useGetPartnerQuery,
} from '@/api/api.slice';
import { useAppDispatch } from '@/app/hooks';
import { AddMeanOfPaymentDialog } from '@/features/means-of-payments/AddMeanOfPaymentDialog.tsx';
import {
  PartnerPaymentMethod,
  useGetPartnerPaymentMethodsQuery,
} from '@/features/means-of-payments/api/useGetPartnerPaymentMethodsQuery.ts';
import { MakeDefaultButton } from '@/features/means-of-payments/MakeDefaultButton.tsx';
import { MeanOfPaymentDescription } from '@/features/means-of-payments/MeanOfPaymentDescription.tsx';
import { setDialogAddPaymentMethodOpen } from '@/features/means-of-payments/MeansOfPayments.slice.ts';
import { WireTransferCallout } from '@/features/means-of-payments/WireTransferCallout.tsx';
import i18n from '@/i18n/i18n';
import {
  Alert,
  Button,
  getMessageFromError,
  Label,
  Paper,
  TableOrCards,
  Typography,
  useSearchPaginationContext,
} from '@arsnovasystems/ars-front-lib';
import { Stack } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const MeansOfPayments = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const searchPagination = useSearchPaginationContext();

  const { data: me } = useGetPartnerQuery();

  const {
    data: meansOfPaymentsCollection,
    isLoading,
    isFetching,
    error,
  } = useGetPartnerPaymentMethodsQuery({
    paginate: searchPagination.pagination,
    search: {
      searchFullText:
        searchPagination.searchString !== '' ?
          searchPagination.searchString
        : undefined,
    },
  });

  const [deletePartnerPaymentMethodTrigger, deletePartnerPaymentMethodResult] =
    useDeletePartnerPaymentMethodMutation();

  const cols = useMemo(() => {
    const columnHelper = createColumnHelper<PartnerPaymentMethod>();

    return [
      columnHelper.accessor(
        (info) => {
          return info.creationDate;
        },
        {
          id: 'creationDate',
          header: t('ik.table.header.creationDate'),
        },
      ),
      columnHelper.accessor('type', {
        cell: (info) => {
          return (
            <Label
              small
              colorKey={info.getValue() + '4'}
              label={t('payment.' + info.getValue())}
            />
          );
        },
        id: 'type',
        header: t('Type'),
      }),
      columnHelper.display({
        id: 'description',
        size: 300,
        header: t('ik.table.header.description'),
        cell: (info) => {
          return <MeanOfPaymentDescription meanOfPayment={info.row.original} />;
        },
      }),
      columnHelper.display({
        id: 'actions',
        meta: {
          flex: 1,
        },
        header: t('ik.table.header.actions'),
        cell: (info) => {
          return (
            <Stack
              direction={'row'}
              sx={{
                gap: 1,
                justifyContent: 'flex-end',
                height: '100%',
              }}
            >
              <Button
                key={info.row.original.id}
                size={'small'}
                color='error'
                variant='outlined'
                disabled={info.row.original.isDefault}
                loading={deletePartnerPaymentMethodResult.isLoading}
                onClick={() => {
                  void deletePartnerPaymentMethodTrigger(info.row.original.id);
                }}
              >
                {t('ik.delete')}
              </Button>
              <MakeDefaultButton data={info.row.original} />
            </Stack>
          );
        },
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  if (!me) {
    return null;
  }

  return (
    <>
      <Paper
        sx={{
          minHeight: 'calc(100% - 300px)',
          paddingBottom: 10,
          padding: 4,
        }}
      >
        <Stack
          direction={'row'}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            variant={'h1'}
            sx={(theme) => ({
              color: theme.palette.neutral.dark,
            })}
          >
            {t('Means of payment')}
          </Typography>
        </Stack>
        <Stack>
          <WireTransferCallout
            sx={{
              marginTop: 6,
            }}
          />

          {deletePartnerPaymentMethodResult.error ?
            <Alert
              sx={{
                marginY: 1,
              }}
            >
              {getMessageFromError(deletePartnerPaymentMethodResult.error)}
            </Alert>
          : null}

          <Button
            variant='contained'
            sx={{
              width: 'fit-content',
              marginTop: 2,
              alignSelf: 'flex-end',
            }}
            onClick={() => {
              dispatch(setDialogAddPaymentMethodOpen(true));
            }}
          >
            {i18n.t('ik.add.a.payment.mean')}
          </Button>
          {error ?
            <Alert>{getMessageFromError(error)}</Alert>
          : null}
          <TableOrCards<PartnerPaymentMethod>
            sx={{ marginTop: 4 }}
            hideToggleButton={true}
            displayMode={'table'}
            loading={isLoading}
            fetching={isFetching}
            data={meansOfPaymentsCollection?.data ?? []}
            columns={cols}
          />
        </Stack>
      </Paper>
      <AddMeanOfPaymentDialog />
    </>
  );
};
